// src/OrionAppTemplate.tsx
import React, { ReactNode, useRef, useEffect, useCallback, useState } from 'react';
import '@/index.css';

export interface OrionNavItem {
  key: string;
  label: string;
  icon?: ReactNode;
  current?: boolean;
  onClick?: () => void;
}

export function OrionThemeProvider({
  children,
  tokensOverrides,
  pitch,
}: {
  children: ReactNode;
  tokensOverrides?: Record<string, string>;
  pitch?: boolean;
}) {
  return (
    <div
      className={`o-theme ${pitch ? 'pitch' : ''}`}
      style={tokensOverrides ? tokensOverrides : {}}
    >
      {children}
    </div>
  );
}

export function OrionAppShell({
  brand,
  nav,
  rightActions,
  children,
}: {
  brand?: { name: string; logo?: ReactNode };
  nav: OrionNavItem[];
  rightActions?: ReactNode;
  children: ReactNode;
  onToggleTheme?: () => void;
}) {
  const navRef = useRef<HTMLDivElement>(null);
  const [scrollEdge, setScrollEdge] = useState({ start: true, end: false });

  /* ── Detect scroll position to toggle edge fade masks ── */
  const syncFades = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setScrollEdge({
      start: el.scrollLeft <= 4,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    });
  }, []);

  /* ── Auto-scroll active pill into view (centred) ── */
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    const left = active.offsetLeft - el.clientWidth / 2 + active.offsetWidth / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    requestAnimationFrame(syncFades);
  }, [nav, syncFades]);

  /* ── Scroll listener for fades ── */
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    syncFades();
    el.addEventListener('scroll', syncFades, { passive: true });
    window.addEventListener('resize', syncFades, { passive: true });
    return () => {
      el.removeEventListener('scroll', syncFades);
      window.removeEventListener('resize', syncFades);
    };
  }, [syncFades]);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)] text-[var(--fg)]">
      {/* ── Header ──
          `overflow-visible` so the ModuleSwitcher dropdown can escape the
          header container and overlay the nav tabs below. The old
          `<span>{brand.name}</span>` was removed: the brand slot is now
          a full custom node (logo + ModuleSwitcher) rendered by Layout,
          and the module name is already visible inside the switcher. */}
      <header className="o-header">
        <div className="flex items-center gap-3 min-w-0 overflow-visible">
          {brand?.logo}
        </div>
        <div className="flex items-center gap-1 shrink-0">{rightActions}</div>
      </header>

      {/* ── Nav · swipeable carousel ── */}
      <div className="o-nav-wrap">
        <div
          className={`o-nav-fade o-nav-fade--l${scrollEdge.start ? ' o-nav-fade--hidden' : ''}`}
          aria-hidden
        />
        <div
          className={`o-nav-fade o-nav-fade--r${scrollEdge.end ? ' o-nav-fade--hidden' : ''}`}
          aria-hidden
        />

        <div ref={navRef} className="o-nav scrollbar-hide" role="tablist">
          {nav.map(item => (
            <button
              key={item.key}
              role="tab"
              aria-selected={item.current}
              data-active={item.current ? 'true' : undefined}
              onClick={item.onClick}
              className={`o-nav-pill${item.current ? ' o-nav-pill--on' : ''}`}
            >
              {item.icon && <span className="o-nav-ico">{item.icon}</span>}
              <span className="o-nav-lbl">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <main className="flex-1 min-h-0">{children}</main>
    </div>
  );
}

export function OrionButton({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className="px-3 py-1 rounded-lg bg-[var(--panel)] text-[var(--fg)] hover:text-[var(--acc1)] border border-[var(--muted)]/20"
    >
      {children}
    </button>
  );
}
